import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAccessToken } from '@hooks/useAccessToken';
import { useAuth0User } from '@hooks/useAuth0User';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import Label from '@components/atoms/label';
import { getSearch } from '@api-service/search';
import { hasSufficientPrivileges, isPaidMember } from '@lib/utils';
import SearchInput from '@components/SearchInput';

import { MEMBERSHIPS, PAID_MEMBERSHIP_LEVELS } from '@lib/constants';
import type { Player as FfhPlayer } from '@api-service/players';
import PlayerChip from '@components/player/chip';
import PlayerImage from '@components/player/image';

const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_STALE_TIME_MS = 5 * 60 * 1000;
const SKELETON_ROW_COUNT = 3;
const NO_ACTIVE_INDEX = -1;
const DEFAULT_LABEL = 'Add a player';

type PlayerSearchProps = {
  hasPaywall: boolean;
  searchId: string;
  showPaywall?: () => void;
  onPlayerClick: (player: FfhPlayer) => void;
  onDeletePlayerChip?: (player: FfhPlayer) => void;
  selectedPlayers?: FfhPlayer[];
  selectedPlayerBorder?: string;
  label?: string;
};

const panelClasses =
  'absolute z-[99] mt-1 w-full min-w-[300px] max-h-[300px] overflow-x-hidden overflow-y-scroll rounded-lg bg-white shadow search-results';

const PlayerSearch = ({
  onPlayerClick,
  hasPaywall,
  searchId,
  showPaywall,
  onDeletePlayerChip,
  selectedPlayers,
  selectedPlayerBorder,
  label = DEFAULT_LABEL
}: PlayerSearchProps) => {
  const { user } = useAuth0User();
  const { accessToken, accessTokenIsFetching } = useAccessToken();
  // The token fetch only runs for logged-in users, so anonymous visitors must
  // not wait on it — accessTokenIsFetching never resolves for them.
  const isAwaitingAccessToken = Boolean(user) && accessTokenIsFetching;
  const hasPaidMembership = isPaidMember(user);
  const hasSufficientMembershipLevel = hasSufficientPrivileges(
    user,
    PAID_MEMBERSHIP_LEVELS[MEMBERSHIPS.STANDARD_MONTHLY_ID].LEVEL
  );
  const isAuthorisedToViewRestrictedPageContent =
    hasPaidMembership && hasSufficientMembershipLevel;
  const restrictContent =
    hasPaywall && !isAuthorisedToViewRestrictedPageContent;

  const [searchInput, setSearchInput] = useState('');
  const [activeIndex, setActiveIndex] = useState(NO_ACTIVE_INDEX);

  const query = searchInput.trim();
  const debouncedQuery = useDebouncedValue(query, SEARCH_DEBOUNCE_MS);

  const wantsResults = query.length >= MIN_SEARCH_LENGTH;
  const isBelowMinLength = query.length > 0 && query.length < MIN_SEARCH_LENGTH;

  // Key cache entries by the user's auth state rather than token presence, so
  // a logged-in user whose token is still resolving never reads results that
  // were cached for an anonymous session.
  const authCacheKey = user ? 'auth' : 'anon';

  const { data, isFetching, isError } = useQuery<FfhPlayer[], Error>({
    queryKey: ['player-search', debouncedQuery, authCacheKey],
    queryFn: () => getSearch(debouncedQuery, accessToken ?? undefined),
    enabled:
      !restrictContent &&
      !isAwaitingAccessToken &&
      debouncedQuery.length >= MIN_SEARCH_LENGTH,
    keepPreviousData: true,
    staleTime: SEARCH_STALE_TIME_MS
  });

  const results = useMemo(
    () => (wantsResults && !isAwaitingAccessToken ? data ?? [] : []),
    [wantsResults, isAwaitingAccessToken, data]
  );

  // The debounced term lags the input by SEARCH_DEBOUNCE_MS, so treat the gap
  // before it catches up (and any in-flight fetch) as "searching" based on the
  // immediate input. This keeps the loading state from flickering to the
  // below-min hint the instant the user reaches three characters.
  const isAwaitingDebounce = debouncedQuery !== query;
  const isSearching =
    wantsResults && (isAwaitingDebounce || isAwaitingAccessToken || isFetching);
  const hasNoMatches =
    wantsResults && !isSearching && !isError && results.length === 0;

  // Reset the keyboard cursor whenever the visible result set changes.
  useEffect(() => {
    setActiveIndex(NO_ACTIVE_INDEX);
  }, [results]);

  const clearSearch = () => {
    setSearchInput('');
    setActiveIndex(NO_ACTIVE_INDEX);
  };

  const selectResult = (player: FfhPlayer) => {
    clearSearch();
    onPlayerClick(player);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (restrictContent) {
      return showPaywall?.();
    }
    setSearchInput(event.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      clearSearch();
      return;
    }
    if (results.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((current) =>
        current >= results.length - 1 ? 0 : current + 1
      );
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) =>
        current <= 0 ? results.length - 1 : current - 1
      );
    } else if (event.key === 'Enter' && activeIndex !== NO_ACTIVE_INDEX) {
      event.preventDefault();
      selectResult(results[activeIndex]);
    }
  };

  const listboxId = `${searchId}-results`;
  const isPanelVisible =
    isBelowMinLength || isSearching || isError || results.length > 0;

  return (
    <div className="w-full max-w-lg lg:max-w-xs">
      <Label htmlFor={searchId} className="sr-only">
        Search
      </Label>
      <div className="relative">
        <SearchInput
          id={searchId}
          handleChange={handleInputChange}
          onKeyDown={handleKeyDown}
          value={searchInput}
          label={label}
          showCloseIcon={searchInput !== ''}
          showSearchIcon
          onClose={clearSearch}
        />

        {selectedPlayers && (
          <div className="my-2">
            {selectedPlayers.map((player) => (
              <PlayerChip
                player={player}
                key={`player-chip-${player.optaId}`}
                handleDelete={onDeletePlayerChip}
                border={selectedPlayerBorder}
              />
            ))}
          </div>
        )}

        {isPanelVisible && (
          <div className={panelClasses}>
            {isBelowMinLength && (
              <p className="px-4 py-3 text-xs text-neutral-600">
                Type at least {MIN_SEARCH_LENGTH} letters to search
              </p>
            )}

            {isError && (
              <p className="px-4 py-3 text-xs text-red-600">
                Couldn&apos;t load players. Try again.
              </p>
            )}

            {!isError && isSearching && results.length === 0 && (
              <ul
                className="divide-y divide-gray-200"
                aria-hidden="true"
                data-cy="player-search-loading"
              >
                {Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => (
                  <li
                    key={`player-search-skeleton-${index}`}
                    className="flex items-center gap-3 p-2"
                  >
                    <span className="h-[50px] w-[50px] shrink-0 rounded-full bg-neutral-200 animate-pulse motion-reduce:animate-none" />
                    <span className="flex-1 space-y-2">
                      <span className="block h-3 w-2/3 rounded bg-neutral-200 animate-pulse motion-reduce:animate-none" />
                      <span className="block h-2.5 w-1/3 rounded bg-neutral-100 animate-pulse motion-reduce:animate-none" />
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {!isError && results.length > 0 && (
              <ul
                id={listboxId}
                role="listbox"
                aria-label="Player search results"
                className={`max-h-72 overflow-y-auto divide-y divide-gray-200 transition-opacity ${
                  isSearching ? 'opacity-60' : ''
                }`}
              >
                {results.map((player, index) => {
                  const isActive = index === activeIndex;
                  return (
                    <li
                      key={player.optaId}
                      role="option"
                      aria-selected={isActive}
                      onClick={() => selectResult(player)}
                      onMouseEnter={() => setActiveIndex(index)}
                      className={`flex items-center gap-3 p-2 cursor-pointer ${
                        isActive ? 'bg-primary-50' : 'hover:bg-neutral-50'
                      }`}
                    >
                      <span className="flex h-[50px] w-[50px] shrink-0 items-center justify-center">
                        <PlayerImage
                          className="h-full"
                          src={player.imageUrl}
                          kitImageUrl={player.kitImageUrl}
                          height={50}
                          width={50}
                          fallbackHeight={40}
                          fallbackWidth={40}
                        />
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-xs font-semibold text-gray-900">
                          {player.displayName ?? player.fullName}
                        </span>
                        <span className="block truncate text-xs text-gray-500">
                          {player.team.fullName}
                        </span>
                        <span className="rounded bg-[#00eebb] px-[10px] py-[3px] text-[11px] leading-4 text-white">
                          {player.seasonStats.points.total}pts
                        </span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}

            {hasNoMatches && (
              <p className="px-4 py-3 text-xs text-neutral-600">
                No players match &ldquo;{query}&rdquo;
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerSearch;
